import { TRUNK_KINDS } from '../constants';
import { digitsOnly, getTrunkKind } from './rows';

export const TRUNK_CHOICES = {
    KEEP: 'keep',
    REMOVE: 'remove',
    OC: 'oc',
    DR: 'dr',
};

// Select value for "leave this field as it is".
export const UNCHANGED = '__UNCHANGED__';

export const OVERSTAMP_MODES = {
    UNCHANGED: 'unchanged',
    DEFAULT: 'default',
    CUSTOM: 'custom',
};

export const OVERSTAMP_PATTERN = /^\+\d{6,15}$/;

export const MESSAGES = {
    pickProfileAndUsage: 'Pick a calling profile and usage type to continue',
    pickProfile: 'Pick a calling profile to continue',
    pickUsage: 'Pick a usage type to continue',
    enterOverstamp: 'Enter an Overstamp number starting with + to continue',
    nothingPicked: 'Nothing will change yet. Pick an option above.',
    alreadyMatches:
        'The selected numbers already have these values, so nothing would change.',
};

export const STATUS_LABELS = { ACTIVE: 'Active', DISABLED: 'Disabled' };

/**
 * Initial modal choices. The trunk to use for each trunk card defaults to the
 * company's first trunk of that type.
 */
export const getInitialChoices = (trunks = []) => ({
    trunk: TRUNK_CHOICES.KEEP,
    ocTrunkID:
        trunks.find((trunk) => getTrunkKind(trunk) === TRUNK_KINDS.OC)?.id ??
        null,
    drTrunkID:
        trunks.find((trunk) => getTrunkKind(trunk) === TRUNK_KINDS.DR)?.id ??
        null,
    callingProfileID: UNCHANGED,
    usageID: UNCHANGED,
    overstampMode: OVERSTAMP_MODES.UNCHANGED,
    overstampValue: '',
});

export const buildLookups = ({ trunks = [], usages = [] }) => ({
    trunksById: new Map(trunks.map((trunk) => [trunk.id, trunk])),
    profilesById: new Map(
        trunks
            .flatMap((trunk) => trunk.callingProfileList ?? [])
            .map((profile) => [profile.id, profile]),
    ),
    usagesById: new Map(usages.map((usage) => [usage.id, usage])),
});

/** The trunk a number ends up on for the chosen trunk card. */
export const getTargetTrunk = (row, choices, { trunksById }) => {
    const toTrunk = (trunkID) => {
        const trunk = trunksById.get(trunkID);
        return trunk
            ? { trunkID, trunkKind: getTrunkKind(trunk), trunkName: trunk.name }
            : { trunkID: null, trunkKind: TRUNK_KINDS.NONE, trunkName: '' };
    };

    switch (choices.trunk) {
        case TRUNK_CHOICES.REMOVE:
            return toTrunk(null);
        case TRUNK_CHOICES.OC:
            return toTrunk(choices.ocTrunkID);
        case TRUNK_CHOICES.DR:
            return toTrunk(choices.drTrunkID);
        default:
            return {
                trunkID: row.trunkID,
                trunkKind: row.trunkKind,
                trunkName: row.trunkName,
            };
    }
};

/**
 * Which steps show and which fields must be picked. "Unchanged" is only
 * offered for a field when every number it affects already has a value:
 * Operator Connect fields look at numbers ending on OC, Overstamp looks at
 * numbers that keep or get a trunk.
 */
export const getRequirements = (rows, choices, lookups) => {
    const targets = rows.map((row) => ({
        row,
        target: getTargetTrunk(row, choices, lookups),
    }));
    const ocRows = targets
        .filter(({ target }) => target.trunkKind === TRUNK_KINDS.OC)
        .map(({ row }) => row);
    const trunkedRows = targets
        .filter(({ target }) => !!target.trunkID)
        .map(({ row }) => row);

    return {
        showOcSettings: ocRows.length > 0,
        profileRequired: ocRows.some((row) => !row.callingProfileID),
        usageRequired: ocRows.some((row) => !row.usageID),
        showOverstamp: trunkedRows.length > 0,
        overstampUnchangedAllowed: trunkedRows.every((row) => !!row.overstamp),
    };
};

/** Unchanged turns into Default when it is not offered. */
export const getOverstampMode = (choices, requirements) =>
    choices.overstampMode === OVERSTAMP_MODES.UNCHANGED &&
    !requirements.overstampUnchangedAllowed
        ? OVERSTAMP_MODES.DEFAULT
        : choices.overstampMode;

const pickValue = (choice, current) =>
    choice && choice !== UNCHANGED ? choice : current;

/**
 * A number's values after the update. Status follows the trunk: a trunk means
 * Active, no trunk means Disabled. Removing the trunk clears the Operator
 * Connect settings and the Overstamp.
 *
 * A number that has no trunk and isn't given one is left exactly as it is.
 * Such numbers (e.g. ones still processing) can carry an old Overstamp, and
 * clearing it would update numbers nobody chose to change.
 */
export const getNextValues = (row, choices, requirements, lookups) => {
    const target = getTargetTrunk(row, choices, lookups);
    if (!target.trunkID && !row.trunkID) {
        return {
            ...target,
            callingProfileID: row.callingProfileID,
            usageID: row.usageID,
            overstamp: row.overstamp,
            status: row.statusLabel,
        };
    }
    if (!target.trunkID) {
        return {
            ...target,
            callingProfileID: '',
            usageID: '',
            overstamp: '',
            status: STATUS_LABELS.DISABLED,
        };
    }

    const isOc = target.trunkKind === TRUNK_KINDS.OC;
    let overstamp;
    switch (getOverstampMode(choices, requirements)) {
        case OVERSTAMP_MODES.DEFAULT:
            overstamp = row.e164;
            break;
        case OVERSTAMP_MODES.CUSTOM:
            // Until something is typed, preview no change rather than a
            // cleared Overstamp
            overstamp = choices.overstampValue || row.overstamp || row.e164;
            break;
        default:
            overstamp = row.overstamp || row.e164;
    }

    return {
        ...target,
        callingProfileID: isOc
            ? pickValue(choices.callingProfileID, row.callingProfileID)
            : '',
        usageID: isOc ? pickValue(choices.usageID, row.usageID) : '',
        overstamp,
        status: STATUS_LABELS.ACTIVE,
    };
};

/**
 * The fields that change for one number, for the "What will change" list.
 * Status is listed only when the number is enabled or disabled, so numbers
 * that are processing don't show a change they won't get.
 *
 * @returns {{ field: string, label: string, from: string, to: string }[]}
 */
export const getRowDiff = (row, next, { profilesById, usagesById }) => {
    const changes = [];
    const add = (field, label, from, to) =>
        changes.push({ field, label, from: from || '', to: to || '' });

    if ((row.trunkID ?? null) !== (next.trunkID ?? null)) {
        add('trunk', 'Trunk', row.trunkName, next.trunkName);
    }
    if ((row.callingProfileID || '') !== (next.callingProfileID || '')) {
        add(
            'callingProfile',
            'Calling profile',
            row.callingProfileName,
            profilesById.get(next.callingProfileID)?.name ??
                next.callingProfileID,
        );
    }
    if ((row.usageID || '') !== (next.usageID || '')) {
        add(
            'usage',
            'Usage type',
            row.usageName,
            usagesById.get(next.usageID)?.displayName ?? next.usageID,
        );
    }
    if ((row.overstamp || '') !== (next.overstamp || '')) {
        add('overstamp', 'Overstamp', row.overstamp, next.overstamp);
    }
    if (!!row.trunkID !== !!next.trunkID) {
        add('status', 'Status', row.statusLabel, next.status);
    }
    return changes;
};

const getMissingMessage = (choices, requirements) => {
    const missing = (choice) => !choice || choice === UNCHANGED;
    const profileMissing =
        requirements.profileRequired && missing(choices.callingProfileID);
    const usageMissing = requirements.usageRequired && missing(choices.usageID);

    if (profileMissing && usageMissing) return MESSAGES.pickProfileAndUsage;
    if (profileMissing) return MESSAGES.pickProfile;
    if (usageMissing) return MESSAGES.pickUsage;
    return null;
};

const isNothingPicked = (choices) =>
    choices.trunk === TRUNK_CHOICES.KEEP &&
    choices.callingProfileID === UNCHANGED &&
    choices.usageID === UNCHANGED &&
    choices.overstampMode === OVERSTAMP_MODES.UNCHANGED;

/**
 * Everything the Bulk Update modal needs to render and decide whether Apply
 * is allowed.
 */
export const getBulkUpdatePlan = (rows, choices, lookups) => {
    const requirements = getRequirements(rows, choices, lookups);
    const items = rows.map((row) => {
        const next = getNextValues(row, choices, requirements, lookups);
        return { row, next, diff: getRowDiff(row, next, lookups) };
    });
    const changing = items.filter((item) => item.diff.length > 0);

    let reason = getMissingMessage(choices, requirements);
    if (
        !reason &&
        requirements.showOverstamp &&
        getOverstampMode(choices, requirements) === OVERSTAMP_MODES.CUSTOM &&
        !OVERSTAMP_PATTERN.test(choices.overstampValue)
    ) {
        reason = MESSAGES.enterOverstamp;
    }
    if (!reason && changing.length === 0) {
        reason = isNothingPicked(choices)
            ? MESSAGES.nothingPicked
            : MESSAGES.alreadyMatches;
    }

    return {
        requirements,
        items,
        changing,
        changingCount: changing.length,
        unchangedCount: items.length - changing.length,
        canApply: !reason,
        reason,
    };
};

/** "Currently selected:" counts by current trunk type. */
export const getTrunkBreakdown = (rows) => {
    const counts = { oc: 0, dr: 0, other: 0, none: 0 };
    rows.forEach((row) => {
        if (row.trunkKind === TRUNK_KINDS.OC) counts.oc += 1;
        else if (row.trunkKind === TRUNK_KINDS.DR) counts.dr += 1;
        else if (row.trunkKind === TRUNK_KINDS.OTHER) counts.other += 1;
        else counts.none += 1;
    });
    return counts;
};

/**
 * The `PUT /PlatformNumber` body: the number's record as listed, with the new
 * trunk, Operator Connect settings and Overstamp. Moving a number onto
 * Operator Connect is confirmed the same way CSV Update does it.
 */
export const buildPayload = (row, next) => {
    const overstampDigits = digitsOnly(next.overstamp);
    return {
        ...row.raw,
        trunkID: next.trunkID ?? null,
        operatorConnectCallingProfileID: next.callingProfileID || null,
        operatorConnectUsageID: next.usageID || null,
        overstampNumber: overstampDigits ? Number(overstampDigits) : null,
        ...(next.trunkKind === TRUNK_KINDS.OC && { isConfirmed: true }),
    };
};

/** Progress text while a DeallocateTrunk request is in flight. */
export const getRemovingTrunksLabel = (count) =>
    `Removing trunks from ${count.toLocaleString()} ${count === 1 ? 'number' : 'numbers'}...`;

/**
 * One-line result of a finished run, e.g. "12 updated, 2 failed, 1 unchanged",
 * leaving out the counts that are zero.
 */
export const getRunSummary = ({ success, failed, ignored, notSent = 0 }) =>
    [
        [success, 'updated'],
        [failed.length, 'failed'],
        [notSent, 'not sent'],
        [ignored, 'unchanged'],
    ]
        .filter(([count]) => count > 0)
        .map(([count, label]) => `${count.toLocaleString()} ${label}`)
        .join(', ') || 'Nothing to update';
